import Carousel from '@components/carousel/dynamicCarousel';
import { getCommunitiesForYouContent$, getHomesForYouContent$ } from 'api/getListingsForYouContent';
import { LocationType, ResultType } from 'features/searchResultsFacets';
import { readCookie } from 'modules/cookieManager';
import { filter, fromEvent, map, switchMap } from 'rxjs';
import { camelCaseObjectKeys } from 'utils/camelCase';

const onDocumentContentLoaded$ = fromEvent(document, 'DOMContentLoaded');
const listingsForYouQuerySelector = '[data-listings-for-you-container]';

/* The `const settings` object is defining properties that store specific query selectors for elements
within the listings section on a webpage. */
const settings = {
  itemListQuerySelector: `${listingsForYouQuerySelector} [data-blog-listing-cards-list]`,
  previousQuerySelector: `${listingsForYouQuerySelector} [data-left-arrow]`,
  nextQuerySelector: `${listingsForYouQuerySelector} [data-right-arrow]`,
};

/* The object is defining two properties: `communities` and `homes`,
each of which is assigned a function that retrieves content for listings related to communities and
homes, respectively. These functions are `getCommunitiesForYouContent$` and
`getHomesForYouContent$`. This object essentially serves as a mapping between different types of
listings content retrieval functions based on the type of listing (communities or homes). */
const getListingForYouSection = {
  communities: getCommunitiesForYouContent$,
  homes: getHomesForYouContent$,
};

/**
 * Initializes a section on a webpage with listings data and
 * loads necessary modules for lead generation and modal form display.
 * @param listingsForYouData - The `listingsForYouData` parameter in the `initListingsFourYouSection`
 * function likely contains data related to listings that are meant to be displayed in a section on a
 * webpage. This data could include information such as property details, images, prices, and any other
 * relevant information about the
 */
const initListingsFourYouSection = function (listingsForYouData) {
  const listingsForYouContainer = document.querySelector(listingsForYouQuerySelector);

  if (listingsForYouData && listingsForYouContainer) {
    const fragment = document.createRange().createContextualFragment(listingsForYouData);
    listingsForYouContainer.appendChild(fragment);

    // loading modules needed to dispatch the lead and modal form
    require('features/leadGenerators');
    require("./standardCard");

    const carousel = new Carousel();

    carousel.setSettings(settings);
    carousel.create();
  }
};

/**
 * Reads personal information from a cookie and
 * updates the blog listings filters search command object accordingly.
 * @param blogListingsFiltersSearchCommand - Takes in a `blogListingsFiltersSearchCommand` object
 * as a parameter and updates its properties based
 * on values retrieved from a personal cookie. Here's a breakdown of how the properties are updated:
 * @returns Returns the `blogListingsFiltersSearchCommand` object after updating its properties based on the values
 * retrieved from the `personalCookie` extracted from the 'Personal' cookie. The properties being
 * updated are `locationType`, `marketId`, `marketStateAbbreviation`, and `marketIds`. The updated
 * `blogListingsFiltersSearchCommand`
 */
function UpdateBlogListingsFiltersSearchCommand(blogListingsFiltersSearchCommand) {

  // Take the context from personal cookie (varnish does not allow take it from service side)
  const personalCookie = readCookie('Personal', true);
  const parameters = new URLSearchParams(personalCookie);

  blogListingsFiltersSearchCommand.locationType = parameters.get('market') ? LocationType.Market.toString() : '';
  blogListingsFiltersSearchCommand.marketId = parameters.get('market') || '';
  blogListingsFiltersSearchCommand.marketStateAbbreviation = parameters.get('state');
  blogListingsFiltersSearchCommand.marketIds = blogListingsFiltersSearchCommand.marketIds || [];

  return blogListingsFiltersSearchCommand;
}

/* This block of code is using RxJS operators to handle asynchronous data flow in a reactive manner. */
onDocumentContentLoaded$.pipe(
  map(() => document.querySelector("[data-component='search-command-data']")),
  filter(blogListingsFiltersSearchCommand => Boolean(blogListingsFiltersSearchCommand)),
  map((blogListingsFiltersSearchCommand) => camelCaseObjectKeys(
    JSON.parse(blogListingsFiltersSearchCommand.innerHTML))),
  filter(blogListingsFiltersSearchCommand => Boolean(ResultType.fromKey(blogListingsFiltersSearchCommand.resultType))),
  switchMap((blogListingsFiltersSearchCommand) => {

    // Business Rule: When we don't have any market information from server,
    // we will use the Personal Cookie information.
    UpdateBlogListingsFiltersSearchCommand(blogListingsFiltersSearchCommand);

    return getListingForYouSection[blogListingsFiltersSearchCommand.resultType](blogListingsFiltersSearchCommand);
  })
).subscribe(initListingsFourYouSection);

